import { useRoute, useRouter } from 'vue-router'
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { get, remove, set } from '@/model/MaCookieStorage'
import { useMaCore } from '@/composables/maCore'
import { getStoreName, safeTrackIdEquals } from '@/base/MaUtils'
import { maLogger } from '@/base/logger'
import Store from '@/base/constants/Store'
import { MAIN_PAGE_ROUTE_NAME, RouteName } from '@/base/constants/route'
import Device from '@/base/constants/Device'

function _getDefaultUser() {
  return {
    name: '',
    surname: '',
    company: '',
    jobFunction: '',
  }
}

function _getDefaultAppFilter() {
  return {
    store: Store.Ios,
    query: '',
  }
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const route = useRoute()
  const router = useRouter()
  const { maRequests } = useMaCore()

  const COMPETITOR_LIMIT = 3
  const KEYWORD_LIMIT = 5
  const COOKIE_NAME = 'MA_Onboarding'

  const STEPS = {
    WELCOME: { name: RouteName.ONBOARDING_WELCOME, number: 0 },
    APP: { name: RouteName.ONBOARDING_SELECT_APP, number: 1 },
    COMPETITORS: { name: RouteName.ONBOARDING_SELECT_COMPETITORS, number: 2 },
    KEYWORDS: { name: RouteName.ONBOARDING_SELECT_KEYWORDS, number: 3 },
    PRICING: { name: RouteName.ONBOARDING_PRICING, number: 4 },
    PAYMENT: { name: RouteName.ONBOARDING_PAYMENT, number: 5 },
    COMPLETE: { name: RouteName.ONBOARDING_COMPLETE, number: 6 },
  }

  const user = ref(_getDefaultUser())
  const userAppFilter = ref(_getDefaultAppFilter())
  const userCountryCode = ref(null)
  const userApp = ref(null)
  const competitorsSearchTerm = ref('')
  const competitorApps = ref([])
  const keywords = ref([])
  const forwardTo = ref(MAIN_PAGE_ROUTE_NAME)

  /**
   * Indicates whether the onboarding complete confetti should be shown.
   */
  const isCompletedOnboarding = ref(false)

  const onboardingPayload = computed(() => {
    return {
      user: user.value,
      userAppFilter: userAppFilter.value,
      userCountryCode: userCountryCode.value,
      userApp: userApp.value,
      competitorsSearchTerm: competitorsSearchTerm.value,
      competitorAppIds: competitorApps.value.map(app => app.trackId),
      keywords: keywords.value,
      forwardTo: forwardTo.value,
    }
  })

  const userAppStore = computed(() => {
    return userApp.value?.trackId ? getStoreName(userApp.value?.trackId) : userAppFilter.value.store
  })

  const userAppDevice = computed(() => {
    return userAppStore.value === Store.Ios ? Device.Iphone : Device.Android
  })

  const currentStep = computed(() => {
    return Object.values(STEPS).find(step => step.name == route.name)
  })

  const achievedStep = computed(() => {
    const { user, userApp, competitorAppIds, keywords } = onboardingPayload.value

    if (!user.name || !user.surname) {
      return STEPS.WELCOME
    }
    else if (!userApp?.trackId) {
      return STEPS.APP
    }
    else if (!competitorAppIds.length) {
      return STEPS.COMPETITORS
    }
    else if (!keywords.length) {
      return STEPS.KEYWORDS
    }

    return STEPS.PRICING
  })

  const setUserInfo = userInfo => user.value = userInfo

  const setUserCountryCode = code => userCountryCode.value = code

  const setUserAppFilter = filter => userAppFilter.value = filter

  const setUserApp = app => userApp.value = app

  const setCompetitorsSearchTerm = searchTerm => competitorsSearchTerm.value = searchTerm

  const setCompetitorApps = apps => competitorApps.value = apps

  const setOnboardingCookie = () => {
    set(COOKIE_NAME, onboardingPayload.value, 30)
  }

  const addCompetitorApp = (app) => {
    competitorApps.value.push(app)
    setOnboardingCookie()
  }

  const removeCompetitorApp = (trackId) => {
    competitorApps.value = competitorApps.value.filter(app => !safeTrackIdEquals(app.trackId, trackId))
    setOnboardingCookie()
  }

  const setKeywords = (keywordList) => {
    keywords.value = keywordList
    setOnboardingCookie()
  }

  const addKeyword = (keyword) => {
    keywords.value.push(keyword)
    setOnboardingCookie()
  }

  const removeKeyword = (keyword) => {
    keywords.value = keywords.value.filter(k => k !== keyword)
  }

  const setForwardTo = path => forwardTo.value = path

  const setShowOnboardingSuccess = status => isCompletedOnboarding.value = status

  const deleteOnboardingCookie = () => remove(COOKIE_NAME)

  const getOnboardingCookie = async () => {
    const userData = get(COOKIE_NAME)

    if (!userData)
      return

    setUserInfo(userData.user)
    setUserCountryCode(userData.userCountryCode)
    setUserAppFilter(userData.userAppFilter)
    setUserApp(userData.userApp)
    setCompetitorsSearchTerm(userData.competitorsSearchTerm)
    setKeywords(userData.keywords || [])
    setForwardTo(userData.forwardTo || forwardTo.value)

    if (!userData.competitorAppIds.length)
      return

    try {
      const apps = await maRequests.AppInfo.getSimpleAppInfo(
        {
          store: userAppStore.value,
          trackIds: userData.competitorAppIds,
          countryCode: userData.userCountryCode,
        },
      )

      setCompetitorApps(apps || [])
    }
    catch (e) {
      maLogger.warn('Failed to fetch competitor apps', e)
    }
  }

  /**
   * Extracts user onboarding data from the query string of the URL.
   *
   * This function is used to parse the URL query string and retrieve
   * user onboarding data, which includes `keywords`, `trackId`, and `countryCode`.
   *
   * @return void
   */
  const getUserOnboardingDataFromQS = async (queryString) => {
    if (!queryString || typeof queryString !== 'object')
      return

    const { trackId, countryCode, keywords, forwardTo } = queryString

    if (keywords) {
      setKeywords(keywords.split(','))
    }

    if (forwardTo) {
      setForwardTo(forwardTo)
    }

    if (!(trackId && countryCode))
      return

    setUserCountryCode(countryCode.toUpperCase())

    try {
      const userApp = (await maRequests.AppInfo.getSimpleAppInfo(
        {
          store: getStoreName(trackId),
          trackIds: [trackId],
          countryCode,
        },
      ))[0]

      setUserAppFilter({
        store: getStoreName(trackId),
        query: '',
      })
      setUserApp(userApp)
      setOnboardingCookie()
    }
    catch (e) {
      maLogger.warn('Failed to fetch user onboarding app', e)
    }
  }

  const goStep = async (step = currentStep.value) => {
    setOnboardingCookie()
    await router.push({
      name: step.name,
      query: route.query,
    })
  }

  const goNextStep = async () => {
    await goStep(Object.values(STEPS).find(step => step.number == currentStep.value.number + 1))
  }

  const goPrevStep = async () => {
    await goStep(Object.values(STEPS).find(step => step.number == currentStep.value.number - 1))
  }

  const $reset = () => {
    user.value = _getDefaultUser()
    userAppFilter.value = _getDefaultAppFilter()
    userCountryCode.value = null
    userApp.value = null
    competitorsSearchTerm.value = ''
    competitorApps.value = []
    keywords.value = []
    forwardTo.value = MAIN_PAGE_ROUTE_NAME
    isCompletedOnboarding.value = false
  }

  return {
    COMPETITOR_LIMIT,
    KEYWORD_LIMIT,
    COOKIE_NAME,
    STEPS,
    user,
    userAppFilter,
    userCountryCode,
    userApp,
    competitorsSearchTerm,
    competitorApps,
    keywords,
    forwardTo,
    isCompletedOnboarding,
    onboardingPayload,
    userAppStore,
    userAppDevice,
    currentStep,
    achievedStep,
    setUserInfo,
    setUserCountryCode,
    setUserAppFilter,
    setUserApp,
    setCompetitorsSearchTerm,
    setCompetitorApps,
    addCompetitorApp,
    removeCompetitorApp,
    setKeywords,
    addKeyword,
    removeKeyword,
    setForwardTo,
    setShowOnboardingSuccess,
    setOnboardingCookie,
    deleteOnboardingCookie,
    getOnboardingCookie,
    getUserOnboardingDataFromQS,
    goStep,
    goNextStep,
    goPrevStep,
    $reset,
  }
})
